import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getMasterWorkFlowStatus, getAllDCA, getSubDCAByDCA, getStatesList, getTaxItCodes, getItCodeGroupDetails,
    getAllGeneralTaxDetails, saveGeneralTax, updateGeneralTax, getVerificationGeneralTaxById,
    getAllGstTaxDetails, saveGstTax, updateGstTax, getVerificationGstTaxById,
} from '../../api/AccountsAPI/taxRegistrationAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Lookups ──────────────────────────────────────────────────────────────────

export const fetchWorkflowStatus = createAsyncThunk('taxRegistration/fetchWorkflow', async (roleId, { rejectWithValue }) => {
    try { const res = await getMasterWorkFlowStatus('118,120', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch workflow status')); }
});

export const fetchTaxDCAs = createAsyncThunk('taxRegistration/fetchDCAs', async (_, { rejectWithValue }) => {
    try { const res = await getAllDCA(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch account heads')); }
});

export const fetchSubDCAsByDCA = createAsyncThunk('taxRegistration/fetchSubDCAs', async (dcaCode, { rejectWithValue }) => {
    try { const res = await getSubDCAByDCA(dcaCode); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub account heads')); }
});

export const fetchStates = createAsyncThunk('taxRegistration/fetchStates', async (_, { rejectWithValue }) => {
    try { const res = await getStatesList(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch states')); }
});

export const fetchTaxItCodes = createAsyncThunk('taxRegistration/fetchTaxItCodes', async (_, { rejectWithValue }) => {
    try { const res = await getTaxItCodes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch IT codes')); }
});

export const fetchItCodeGroupDetails = createAsyncThunk('taxRegistration/fetchItCodeGroupDetails', async (itId, { rejectWithValue }) => {
    try { const res = await getItCodeGroupDetails(itId); return res?.Data || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch IT code group details')); }
});

// ── General Tax ──────────────────────────────────────────────────────────────

export const fetchGeneralTaxGrid = createAsyncThunk('taxRegistration/fetchGeneralGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllGeneralTaxDetails(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch general taxes')); }
});

export const fetchGeneralTaxById = createAsyncThunk('taxRegistration/fetchGeneralById', async (taxId, { rejectWithValue }) => {
    try { const res = await getVerificationGeneralTaxById(taxId); return res?.Data || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch tax details')); }
});

export const submitGeneralTax = createAsyncThunk('taxRegistration/submitGeneral', async (payload, { rejectWithValue }) => {
    try {
        const isEdit = payload.Action === 'Update';
        const res = isEdit ? await updateGeneralTax(payload) : await saveGeneralTax(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save tax');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save tax')); }
});

// ── GST Tax ──────────────────────────────────────────────────────────────────

export const fetchGstTaxGrid = createAsyncThunk('taxRegistration/fetchGstGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllGstTaxDetails(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch GST taxes')); }
});

export const fetchGstTaxById = createAsyncThunk('taxRegistration/fetchGstById', async (taxId, { rejectWithValue }) => {
    try { const res = await getVerificationGstTaxById(taxId); return res?.Data || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch GST details')); }
});

export const submitGstTax = createAsyncThunk('taxRegistration/submitGst', async (payload, { rejectWithValue }) => {
    try {
        const isEdit = payload.Action === 'Update';
        const res = isEdit ? await updateGstTax(payload) : await saveGstTax(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save GST');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save GST')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    workflowStatus: [], workflowStatusLoading: false,
    dcas: [], dcasLoading: false,
    subDcas: [], subDcasLoading: false,
    states: [], statesLoading: false,
    taxItCodes: [], taxItCodesLoading: false,
    itCodeGroupDetails: null, itCodeGroupDetailsLoading: false,

    generalGrid: [], generalGridLoading: false, generalGridError: null,
    generalById: null, generalByIdLoading: false,
    generalSaveLoading: false, generalSaveResult: null, generalSaveError: null,

    gstGrid: [], gstGridLoading: false, gstGridError: null,
    gstById: null, gstByIdLoading: false,
    gstSaveLoading: false, gstSaveResult: null, gstSaveError: null,
};

const taxRegistrationSlice = createSlice({
    name: 'taxRegistration',
    initialState,
    reducers: {
        clearSubDcas: (s) => { s.subDcas = []; },
        clearItCodeGroupDetails: (s) => { s.itCodeGroupDetails = null; },
        clearGeneralById: (s) => { s.generalById = null; },
        clearGeneralSaveResult: (s) => { s.generalSaveResult = null; s.generalSaveError = null; },
        clearGstById: (s) => { s.gstById = null; },
        clearGstSaveResult: (s) => { s.gstSaveResult = null; s.gstSaveError = null; },
        resetTaxRegistration: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkflowStatus.pending, (s) => { s.workflowStatusLoading = true; })
            .addCase(fetchWorkflowStatus.fulfilled, (s, a) => { s.workflowStatusLoading = false; s.workflowStatus = a.payload; })
            .addCase(fetchWorkflowStatus.rejected, (s) => { s.workflowStatusLoading = false; });

        builder
            .addCase(fetchTaxDCAs.pending, (s) => { s.dcasLoading = true; })
            .addCase(fetchTaxDCAs.fulfilled, (s, a) => { s.dcasLoading = false; s.dcas = a.payload; })
            .addCase(fetchTaxDCAs.rejected, (s) => { s.dcasLoading = false; });

        builder
            .addCase(fetchSubDCAsByDCA.pending, (s) => { s.subDcasLoading = true; })
            .addCase(fetchSubDCAsByDCA.fulfilled, (s, a) => { s.subDcasLoading = false; s.subDcas = a.payload; })
            .addCase(fetchSubDCAsByDCA.rejected, (s) => { s.subDcasLoading = false; });

        builder
            .addCase(fetchStates.pending, (s) => { s.statesLoading = true; })
            .addCase(fetchStates.fulfilled, (s, a) => { s.statesLoading = false; s.states = a.payload; })
            .addCase(fetchStates.rejected, (s) => { s.statesLoading = false; });

        builder
            .addCase(fetchTaxItCodes.pending, (s) => { s.taxItCodesLoading = true; })
            .addCase(fetchTaxItCodes.fulfilled, (s, a) => { s.taxItCodesLoading = false; s.taxItCodes = a.payload; })
            .addCase(fetchTaxItCodes.rejected, (s) => { s.taxItCodesLoading = false; });

        builder
            .addCase(fetchItCodeGroupDetails.pending, (s) => { s.itCodeGroupDetailsLoading = true; })
            .addCase(fetchItCodeGroupDetails.fulfilled, (s, a) => { s.itCodeGroupDetailsLoading = false; s.itCodeGroupDetails = a.payload; })
            .addCase(fetchItCodeGroupDetails.rejected, (s) => { s.itCodeGroupDetailsLoading = false; s.itCodeGroupDetails = null; });

        builder
            .addCase(fetchGeneralTaxGrid.pending, (s) => { s.generalGridLoading = true; s.generalGridError = null; })
            .addCase(fetchGeneralTaxGrid.fulfilled, (s, a) => { s.generalGridLoading = false; s.generalGrid = a.payload; })
            .addCase(fetchGeneralTaxGrid.rejected, (s, a) => { s.generalGridLoading = false; s.generalGridError = a.payload; });

        builder
            .addCase(fetchGeneralTaxById.pending, (s) => { s.generalByIdLoading = true; })
            .addCase(fetchGeneralTaxById.fulfilled, (s, a) => { s.generalByIdLoading = false; s.generalById = a.payload; })
            .addCase(fetchGeneralTaxById.rejected, (s) => { s.generalByIdLoading = false; s.generalById = null; });

        builder
            .addCase(submitGeneralTax.pending, (s) => { s.generalSaveLoading = true; s.generalSaveError = null; s.generalSaveResult = null; })
            .addCase(submitGeneralTax.fulfilled, (s, a) => { s.generalSaveLoading = false; s.generalSaveResult = a.payload; })
            .addCase(submitGeneralTax.rejected, (s, a) => { s.generalSaveLoading = false; s.generalSaveError = a.payload; });

        builder
            .addCase(fetchGstTaxGrid.pending, (s) => { s.gstGridLoading = true; s.gstGridError = null; })
            .addCase(fetchGstTaxGrid.fulfilled, (s, a) => { s.gstGridLoading = false; s.gstGrid = a.payload; })
            .addCase(fetchGstTaxGrid.rejected, (s, a) => { s.gstGridLoading = false; s.gstGridError = a.payload; });

        builder
            .addCase(fetchGstTaxById.pending, (s) => { s.gstByIdLoading = true; })
            .addCase(fetchGstTaxById.fulfilled, (s, a) => { s.gstByIdLoading = false; s.gstById = a.payload; })
            .addCase(fetchGstTaxById.rejected, (s) => { s.gstByIdLoading = false; s.gstById = null; });

        builder
            .addCase(submitGstTax.pending, (s) => { s.gstSaveLoading = true; s.gstSaveError = null; s.gstSaveResult = null; })
            .addCase(submitGstTax.fulfilled, (s, a) => { s.gstSaveLoading = false; s.gstSaveResult = a.payload; })
            .addCase(submitGstTax.rejected, (s, a) => { s.gstSaveLoading = false; s.gstSaveError = a.payload; });
    },
});

export const {
    clearSubDcas, clearItCodeGroupDetails, clearGeneralById, clearGeneralSaveResult,
    clearGstById, clearGstSaveResult, resetTaxRegistration,
} = taxRegistrationSlice.actions;

export const selectTaxRegistration = (s) => s.taxRegistration;

export default taxRegistrationSlice.reducer;
